import { useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ComputeResponse,
  KpiChartType,
} from "../../../../domain/types/company/company-kpi";

/**
 * Y축 최대 — 데이터 최대치보다 ~20% 위 헤드룸을 주되 깔끔한 수로 올림.
 * 작은 값(<10)은 정수 올림(1→2), 큰 값은 1·1.2·1.5·2·2.5·3·4·5·6·8·10 단계로.
 * 예) 100→120, 1→2, 4016→5000, 80→100.
 */
function axisMax(dataMax: number): number {
  if (!Number.isFinite(dataMax) || dataMax <= 0) return 1;
  const target = dataMax * 1.2;
  if (dataMax < 10) return Math.ceil(target);
  const mag = Math.pow(10, Math.floor(Math.log10(target)));
  const norm = target / mag;
  const steps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  const nice = steps.find((s) => s >= norm) ?? 10;
  return nice * mag;
}

/**
 * 버킷 시각(ISO datetime/날짜) → 축·툴팁 표시 라벨. interval 별 분기.
 *  - 분: HH:mm / 시간: HH시 / 일·주: M.D / 월: YYYY.MM
 *  - full(툴팁): 분/시간은 날짜를 덧붙임(M/D HH:mm).
 */
export function formatBucket(bucket: string, interval: string, full = false): string {
  const [datePart, timePart] = String(bucket).split("T");
  const [y, m, d] = datePart.split("-");
  const hm = (timePart ?? "00:00").slice(0, 5);
  if (interval === "MINUTE") return full ? `${m}/${d} ${hm}` : hm;
  if (interval === "HOUR") return full ? `${m}/${d} ${hm.slice(0, 2)}시` : `${hm.slice(0, 2)}시`;
  if (interval === "MONTH") return `${y}.${m}`;
  return `${m}.${d}`;
}

/** 시리즈 색상 (디자인 가이드 Primary + 변주). */
export const SERIES_COLORS = [
  "#4285F4",
  "#F2994A",
  "#27AE60",
  "#9B51E0",
  "#F24A4A",
  "#56CCF2",
];

/**
 * ComputeResponse → recharts 데이터.
 * buckets(공유 x축) 각 행에 시리즈별 값. 데이터 없는 버킷은 0으로 채움
 * (백엔드가 0 이하 값을 시리즈에서 제외하므로, 선이 끊기지 않고 0을 지나도록).
 */
export function buildChartData(
  res: ComputeResponse,
): Array<Record<string, string | number>> {
  return res.buckets.map((bucket) => {
    const row: Record<string, string | number> = { bucket };
    res.series.forEach((s) => {
      const pt = s.points.find((p) => p.bucketStart === bucket);
      row[s.label] = pt ? pt.value : 0;
    });
    return row;
  });
}

interface Props {
  response: ComputeResponse;
  chartType: KpiChartType;
  height?: number | string;
  /** 점(버킷) 클릭 시 그 버킷 날짜(yyyy-MM-dd)를 전달 — 드릴다운용. */
  onPointClick?: (bucket: string) => void;
  /** 그래프를 드래그한 구간(시작·끝 버킷)을 전달 — 기간 확대(재조회)용. */
  onZoom?: (startBucket: string, endBucket: string) => void;
}

type ChartMouseState = { activeLabel?: string } | null;

/** 카드/에디터 공용 차트 — BAR/LINE, 다중 시리즈. 드래그 시 구간 확대(onZoom). */
export const ComputeChart = ({ response, chartType, height = 240, onPointClick, onZoom }: Props) => {
  const chartData = buildChartData(response);

  // 드래그-줌 — 마우스로 끌어 잡은 구간([refLeft, refRight])을 놓으면 onZoom 으로 전달.
  const [refLeft, setRefLeft] = useState<string | null>(null);
  const [refRight, setRefRight] = useState<string | null>(null);
  const draggedRef = useRef(false); // 드래그 직후 click(드릴다운) 억제용

  const handleMouseDown = (state: ChartMouseState) => {
    if (!onZoom) return;
    const b = state?.activeLabel;
    if (b) {
      setRefLeft(b);
      setRefRight(null);
    }
  };
  const handleMouseMove = (state: ChartMouseState) => {
    if (!onZoom || refLeft == null) return;
    const b = state?.activeLabel;
    if (b) setRefRight(b);
  };
  const handleMouseUp = () => {
    if (refLeft != null && refRight != null && refLeft !== refRight) {
      const [a, b] = refLeft < refRight ? [refLeft, refRight] : [refRight, refLeft];
      draggedRef.current = true;
      onZoom?.(a, b);
    }
    setRefLeft(null);
    setRefRight(null);
  };
  const handleClick = (state: ChartMouseState) => {
    if (draggedRef.current) {
      draggedRef.current = false; // 드래그였으면 클릭(드릴다운) 무시
      return;
    }
    const bucket = state?.activeLabel;
    if (bucket && onPointClick) onPointClick(bucket);
  };
  const cursor = onZoom ? "crosshair" : onPointClick ? "pointer" : "default";

  if (response.series.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-[13px] text-[#888]"
        style={{ height }}
      >
        지표를 추가하세요
      </div>
    );
  }

  const common = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
      <XAxis
        dataKey="bucket"
        tick={{ fontSize: 11, fill: "#888888" }}
        minTickGap={16}
        axisLine={{ stroke: "#E0E0E0" }}
        tickLine={false}
        tickFormatter={(v: string) => formatBucket(v, response.interval)}
      />
      <YAxis
        tick={{ fontSize: 11, fill: "#888888" }}
        width={40}
        axisLine={false}
        tickLine={false}
        domain={[0, (dataMax: number) => axisMax(dataMax)]}
      />
      <Tooltip
        contentStyle={{ fontSize: 12, borderColor: "#E0E0E0", borderRadius: 8 }}
        labelFormatter={(v) => formatBucket(String(v), response.interval, true)}
      />
      <Legend
        iconType="square"
        iconSize={10}
        verticalAlign="top"
        align="center"
        wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
      />
      {refLeft && refRight && (
        <ReferenceArea x1={refLeft} x2={refRight} strokeOpacity={0.3} fill="#4285F4" fillOpacity={0.12} />
      )}
    </>
  );

  const dragHandlers = onZoom
    ? { onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp }
    : {};

  return (
    <ResponsiveContainer width="100%" height={height}>
      {chartType === "LINE" ? (
        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }} onClick={handleClick} {...dragHandlers} style={{ cursor }}>
          {common}
          {response.series.map((s, i) => (
            <Line
              key={s.label}
              type="monotone"
              dataKey={s.label}
              stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 2 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      ) : (
        <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }} onClick={handleClick} {...dragHandlers} style={{ cursor }}>
          {common}
          {response.series.map((s, i) => (
            <Bar
              key={s.label}
              dataKey={s.label}
              fill={SERIES_COLORS[i % SERIES_COLORS.length]}
              radius={[2, 2, 0, 0]}
            />
          ))}
        </BarChart>
      )}
    </ResponsiveContainer>
  );
};